import {
  CATALOG_FIELDS,
  CATALOG_KINDS,
  CATALOG_LABELS,
  catalogToCsv,
  parseLocaleNumber,
  type CatalogField,
  type CatalogImportResult,
  type CatalogItemDTO,
  type CatalogKind,
} from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Combobox } from '../components/Combobox';
import { useCampaignId } from '../hooks';
import { t } from '../i18n';
import { classNames, fuzzyFilter } from '../util';

const fmt = (n: number | undefined) => (n === undefined ? '' : n.toLocaleString('de-DE', { maximumFractionDigits: 3 }));

/** Wert eines Felds aus einem Eintrag. */
function valueOf(f: CatalogField, it: Pick<CatalogItemDTO, 'name' | 'value' | 'weight' | 'data'>): string | number | boolean | undefined {
  if (f.key === 'name') return it.name;
  if (f.inData) return it.data[f.key];
  return it[f.key as 'value' | 'weight'];
}

/** Änderung eines Felds als PATCH-Daten. */
function patchOf(f: CatalogField, it: CatalogItemDTO, v: string | number | boolean | undefined) {
  if (f.key === 'name') return { name: String(v ?? '') };
  if (!f.inData) return { [f.key]: v === undefined ? null : v };
  const data = { ...it.data };
  if (v === undefined || v === '' || v === false) delete data[f.key];
  else data[f.key] = v;
  return { data };
}

/** CSV-Datei lesen: UTF-8, sonst Windows-1252 (so speichert deutsches Excel „CSV (Trennzeichen-getrennt)“). */
async function readCsvFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1252').decode(buf);
  }
}

function Cell(props: { field: CatalogField; value: string | number | boolean | undefined; onCommit: (v: string | number | boolean | undefined) => void }) {
  const { field, value } = props;
  const [text, setText] = useState(typeof value === 'number' ? fmt(value) : String(value ?? ''));
  useEffect(() => setText(typeof value === 'number' ? fmt(value) : String(value ?? '')), [value]);
  const options = useMemo(() => (field.options ?? []).map((o) => ({ key: o, title: o })), [field.options]);

  if (field.type === 'boolean') {
    return <input type="checkbox" checked={value === true} aria-label={field.label} onChange={(e) => props.onCommit(e.target.checked)} />;
  }
  const commit = (raw = text) => {
    const v = field.type === 'number' ? parseLocaleNumber(raw) : raw.trim() || undefined;
    if (field.type === 'number' && raw.trim() !== '' && v === undefined) return setText(typeof value === 'number' ? fmt(value) : '');
    if (v !== value && !(v === undefined && (value === undefined || value === ''))) props.onCommit(v);
  };
  if (options.length) {
    return (
      <Combobox
        className="cat-input"
        value={text}
        onChange={setText}
        options={options}
        aria-label={field.label}
        onPick={(o) => {
          setText(o.title);
          commit(o.title);
        }}
        onEnter={() => commit()}
        onBlur={() => commit()}
      />
    );
  }
  return (
    <input
      className={classNames('cat-input', field.type === 'number' && 'num')}
      inputMode={field.type === 'number' ? 'decimal' : undefined}
      aria-label={field.label}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => commit()}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

function ImportPanel(props: { cid: string; kind: CatalogKind; onDone: (r: CatalogImportResult) => void; onCancel: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fields = CATALOG_FIELDS[props.kind];
  const run = async () => {
    if (!file) return;
    if (mode === 'replace' && !confirm(t('catalog.replaceConfirm', { kind: CATALOG_LABELS[props.kind] }))) return;
    setBusy(true);
    setError(null);
    try {
      props.onDone(await api.importCatalog(props.cid, props.kind, mode, await readCsvFile(file)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card cat-import">
      <p className="small muted">{t('catalog.importHint', { columns: fields.map((f) => f.label).join(' ; ') })}</p>
      <input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <div className="row wrap">
        <label className="inline">
          <input type="radio" checked={mode === 'merge'} onChange={() => setMode('merge')} /> {t('catalog.modeMerge')}
        </label>
        <label className="inline">
          <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} /> {t('catalog.modeReplace')}
        </label>
      </div>
      <div className="row">
        <button className="primary" disabled={!file || busy} onClick={() => void run()}>
          {busy ? t('common.loading') : t('catalog.import')}
        </button>
        <button onClick={props.onCancel}>{t('common.cancel')}</button>
      </div>
      {error && <p className="error small">{error}</p>}
    </div>
  );
}

export function CatalogPage() {
  const cid = useCampaignId();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const kind = (CATALOG_KINDS as readonly string[]).includes(params.get('kind') ?? '') ? (params.get('kind') as CatalogKind) : 'gear';
  const q = useQuery({ queryKey: ['catalog', cid, kind], queryFn: () => api.catalog(cid, kind) });
  const [filter, setFilter] = useState('');
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState<CatalogImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const fields = CATALOG_FIELDS[kind];

  useEffect(() => {
    document.title = `${t('catalog.title')} · Kartothek`;
  }, []);
  useEffect(() => {
    setReport(null);
    setImporting(false);
    setError(null);
  }, [kind]);

  const items = useMemo(() => {
    const list = (q.data ?? []).map((x) => ({ ...x, title: x.name }));
    return filter.trim() ? fuzzyFilter(list, filter, 1000) : list;
  }, [q.data, filter]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['catalog', cid] });
    void qc.invalidateQueries({ queryKey: ['items', cid] });
  };
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
    refresh();
  };

  const update = (it: CatalogItemDTO, f: CatalogField, v: string | number | boolean | undefined) => {
    if (f.key === 'name' && !v) return;
    void run(async () => {
      const res = await api.updateCatalogItem(cid, it.id, patchOf(f, it, v));
      qc.setQueryData<CatalogItemDTO[]>(['catalog', cid, kind], (old) => old?.map((x) => (x.id === res.id ? res : x)));
    });
  };

  const add = () => {
    const name = newName.trim();
    if (!name) return;
    void run(async () => {
      await api.createCatalogItem(cid, { kind, name, data: {} });
      setNewName('');
    });
  };

  const exportCsv = () => {
    const csv = catalogToCsv(kind, q.data ?? []);
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `katalog-${CATALOG_LABELS[kind].toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page-wide">
      <h1>{t('catalog.title')}</h1>
      <p className="muted small">{t('catalog.intro')}</p>
      <div className="tabs" role="tablist">
        {CATALOG_KINDS.map((k) => (
          <button key={k} role="tab" aria-selected={k === kind} className={classNames('tab', k === kind && 'on')} onClick={() => setParams({ kind: k })}>
            {CATALOG_LABELS[k]}
          </button>
        ))}
      </div>
      <div className="cat-toolbar">
        <input className="cat-filter" placeholder={t('catalog.filter')} value={filter} onChange={(e) => setFilter(e.target.value)} />
        <span className="muted small">{t('catalog.count', { n: q.data?.length ?? 0 })}</span>
        <span className="spacer" />
        <button onClick={() => setImporting((x) => !x)}>⇪ {t('catalog.import')}</button>
        <button onClick={exportCsv} disabled={!q.data?.length}>
          ⇩ {t('catalog.export')}
        </button>
        <button
          title={t('catalog.defaultsHint')}
          onClick={() =>
            void run(async () => {
              const r = await api.catalogDefaults(cid, kind);
              setReport({ added: r.added, updated: 0, removed: 0, errors: [] });
            })
          }
        >
          {t('catalog.defaults')}
        </button>
        <button
          className="danger"
          disabled={!q.data?.length}
          onClick={() => {
            if (!confirm(t('catalog.clearConfirm', { kind: CATALOG_LABELS[kind] }))) return;
            void run(() => api.clearCatalog(cid, kind));
          }}
        >
          {t('catalog.clear')}
        </button>
      </div>
      {importing && (
        <ImportPanel
          kind={kind}
          cid={cid}
          onCancel={() => setImporting(false)}
          onDone={(r) => {
            setImporting(false);
            setReport(r);
            refresh();
          }}
        />
      )}
      {report && (
        <div className="card cat-report small">
          {t('catalog.report', { added: report.added, updated: report.updated, removed: report.removed })}
          {report.errors.length > 0 && (
            <ul>
              {report.errors.map((e, i) => (
                <li key={i} className="warn">
                  {e}
                </li>
              ))}
            </ul>
          )}
          <button className="small" onClick={() => setReport(null)}>
            {t('common.close')}
          </button>
        </div>
      )}
      {error && <div className="error banner">{error}</div>}
      <div className="cat-scroll">
        <table className="catalog">
          <thead>
            <tr>
              {fields.map((f) => (
                <th key={f.key} className={f.type === 'number' ? 'num' : undefined}>
                  {f.label}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                {fields.map((f) => (
                  <td key={f.key} className={classNames(`cat-${f.key}`, f.type === 'number' && 'num')}>
                    <Cell field={f} value={valueOf(f, it)} onCommit={(v) => update(it, f, v)} />
                  </td>
                ))}
                <td>
                  <button
                    className="icon-btn danger"
                    title={t('common.delete')}
                    onClick={() => void run(() => api.deleteCatalogItem(cid, it.id))}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            <tr className="cat-new">
              <td colSpan={fields.length + 1}>
                <input
                  value={newName}
                  placeholder={t('catalog.newPlaceholder')}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && add()}
                />
                <button className="primary" disabled={!newName.trim()} onClick={add}>
                  + {t('catalog.add')}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
